import { compareCodePoints, compareForSort } from './collator.js';
/**
 * ISO 3166-1 alpha-2, with the names resolved through `Intl.DisplayNames` rather than stored.
 *
 * The codes are static because they are DATA: a country list that changed with the platform's ICU
 * version would mean a value accepted on one machine and rejected on another, and a stored country
 * code whose meaning drifted. The NAMES are resolved through the platform because Node 24 ships
 * full ICU and the browser has it too, so translating 250 country names into Spanish and English by
 * hand would be 500 strings to maintain, keep in parity and get wrong.
 *
 * **How this list was derived, so it can be re-derived rather than trusted.** ICU on Node 24 knows
 * 280 two-letter regions. Thirty-one of those are not officially assigned ISO 3166-1 codes:
 * fourteen are deprecated or transitionally reserved (`AN` `BU` `CS` `DD` `DY` `FX` `HV` `NH` `RH`
 * `SU` `TP` `VD` `YD` `YU` `ZR`), eleven are exceptionally reserved or macro-regions (`AC` `CP`
 * `CQ` `DG` `EA` `EU` `EZ` `IC` `TA` `UK` `UN`), and the rest are CLDR's own placeholders (`QO`
 * `XA` `XB` `ZZ`). Removing them leaves exactly the 249 officially assigned codes below, which is
 * the number ISO publishes.
 *
 * **`XK` is added on purpose and is the one code here ISO has not assigned.** Kosovo issues
 * passports, and a facility that cannot record the country on a passport it is holding has a data
 * quality problem rather than a standards problem. `XK` is the user-assigned code the EU, the IMF,
 * the World Bank and the phone-number library all use for it. It is kept in its own constant so
 * that the officially assigned set stays exactly what ISO says it is.
 */
/** The 249 officially assigned ISO 3166-1 alpha-2 codes, in ascending order. */
export const ISO_3166_1_ALPHA_2 = [
    'AD',
    'AE',
    'AF',
    'AG',
    'AI',
    'AL',
    'AM',
    'AO',
    'AQ',
    'AR',
    'AS',
    'AT',
    'AU',
    'AW',
    'AX',
    'AZ',
    'BA',
    'BB',
    'BD',
    'BE',
    'BF',
    'BG',
    'BH',
    'BI',
    'BJ',
    'BL',
    'BM',
    'BN',
    'BO',
    'BQ',
    'BR',
    'BS',
    'BT',
    'BV',
    'BW',
    'BY',
    'BZ',
    'CA',
    'CC',
    'CD',
    'CF',
    'CG',
    'CH',
    'CI',
    'CK',
    'CL',
    'CM',
    'CN',
    'CO',
    'CR',
    'CU',
    'CV',
    'CW',
    'CX',
    'CY',
    'CZ',
    'DE',
    'DJ',
    'DK',
    'DM',
    'DO',
    'DZ',
    'EC',
    'EE',
    'EG',
    'EH',
    'ER',
    'ES',
    'ET',
    'FI',
    'FJ',
    'FK',
    'FM',
    'FO',
    'FR',
    'GA',
    'GB',
    'GD',
    'GE',
    'GF',
    'GG',
    'GH',
    'GI',
    'GL',
    'GM',
    'GN',
    'GP',
    'GQ',
    'GR',
    'GS',
    'GT',
    'GU',
    'GW',
    'GY',
    'HK',
    'HM',
    'HN',
    'HR',
    'HT',
    'HU',
    'ID',
    'IE',
    'IL',
    'IM',
    'IN',
    'IO',
    'IQ',
    'IR',
    'IS',
    'IT',
    'JE',
    'JM',
    'JO',
    'JP',
    'KE',
    'KG',
    'KH',
    'KI',
    'KM',
    'KN',
    'KP',
    'KR',
    'KW',
    'KY',
    'KZ',
    'LA',
    'LB',
    'LC',
    'LI',
    'LK',
    'LR',
    'LS',
    'LT',
    'LU',
    'LV',
    'LY',
    'MA',
    'MC',
    'MD',
    'ME',
    'MF',
    'MG',
    'MH',
    'MK',
    'ML',
    'MM',
    'MN',
    'MO',
    'MP',
    'MQ',
    'MR',
    'MS',
    'MT',
    'MU',
    'MV',
    'MW',
    'MX',
    'MY',
    'MZ',
    'NA',
    'NC',
    'NE',
    'NF',
    'NG',
    'NI',
    'NL',
    'NO',
    'NP',
    'NR',
    'NU',
    'NZ',
    'OM',
    'PA',
    'PE',
    'PF',
    'PG',
    'PH',
    'PK',
    'PL',
    'PM',
    'PN',
    'PR',
    'PS',
    'PT',
    'PW',
    'PY',
    'QA',
    'RE',
    'RO',
    'RS',
    'RU',
    'RW',
    'SA',
    'SB',
    'SC',
    'SD',
    'SE',
    'SG',
    'SH',
    'SI',
    'SJ',
    'SK',
    'SL',
    'SM',
    'SN',
    'SO',
    'SR',
    'SS',
    'ST',
    'SV',
    'SX',
    'SY',
    'SZ',
    'TC',
    'TD',
    'TF',
    'TG',
    'TH',
    'TJ',
    'TK',
    'TL',
    'TM',
    'TN',
    'TO',
    'TR',
    'TT',
    'TV',
    'TW',
    'TZ',
    'UA',
    'UG',
    'UM',
    'US',
    'UY',
    'UZ',
    'VA',
    'VC',
    'VE',
    'VG',
    'VI',
    'VN',
    'VU',
    'WF',
    'WS',
    'YE',
    'YT',
    'ZA',
    'ZM',
    'ZW',
];
/**
 * Codes this product accepts that ISO has not assigned, each with a reason.
 *
 * `XK`: Kosovo. Not assigned by ISO, in wide use everywhere else, and the holder of a Kosovar
 * passport is a person this system has to be able to register.
 *
 * `XX`: no nationality, or one nobody has established yet. This facility registers applicants for
 * international protection, among whom statelessness is a recognised status with its own procedure
 * rather than a gap in the paperwork — and the field is labelled "Nacionalidad", so a list with no
 * value for it would be a question the record cannot answer. It is deliberately ONE code covering
 * both "apátrida" and "sin determinar": the difference between them is a legal finding the centre
 * does not make, and offering two values would invite an operator to guess which one applies.
 * ICAO's machine-readable travel documents use the same shape of reservation (`XXA` for a stateless
 * person, `XXX` for unspecified), which is where the letters come from; the two-letter space has no
 * standard for it, so `XX` is user-assigned exactly as `XK` is. It is NOT one of the CLDR
 * placeholders this file removes on purpose — `QO`, `XA`, `XB` and `ZZ` are, and `XX` is not a
 * region ICU knows at all, which is why it needs {@link SUPPLIED_COUNTRY_NAMES} below.
 */
export const USER_ASSIGNED_COUNTRY_CODES = ['XK', 'XX'];
/** Every code a `country` field may hold, in ascending order. */
export const COUNTRY_CODES = [
    ...ISO_3166_1_ALPHA_2,
    ...USER_ASSIGNED_COUNTRY_CODES,
].sort(compareCodePoints);
const COUNTRY_CODE_SET = new Set(COUNTRY_CODES);
/** Whether a string is a country code this product accepts. Case-sensitive: codes are upper case. */
export function isCountryCode(value) {
    return COUNTRY_CODE_SET.has(value);
}
/**
 * One `Intl.DisplayNames` per locale, built once.
 *
 * Constructing one is expensive enough that doing it per row of a 250-row select would show, and
 * the objects are immutable, so a module-level cache is both safe and the obvious thing.
 */
const DISPLAY_NAMES = new Map();
function displayNamesFor(locale) {
    const existing = DISPLAY_NAMES.get(locale);
    if (existing !== undefined) {
        return existing;
    }
    const created = new Intl.DisplayNames([locale], { type: 'region', fallback: 'code' });
    DISPLAY_NAMES.set(locale, created);
    return created;
}
/**
 * The country's name in one language.
 *
 * `fallback: 'code'` means an unknown code returns itself rather than `undefined`, so a value
 * stored years ago still renders as something a person can read instead of as a blank cell.
 */
export function countryName(code, locale) {
    const supplied = SUPPLIED_COUNTRY_NAMES[code];
    if (supplied !== undefined) {
        // `locale` is `es` or `en` everywhere in this product; anything else falls to English rather
        // than to the raw code, because a code is the one answer this table exists to avoid.
        return supplied[locale.startsWith('es') ? 'es' : 'en'];
    }
    return displayNamesFor(locale).of(code) ?? code;
}
/**
 * The names this product supplies because the platform has none.
 *
 * Only ever reached for a code ICU does not know, which today is `XX` alone: `XK` is in CLDR and
 * resolves through `Intl.DisplayNames` like every ISO code. **This is the one place in this file
 * where a country name is a string rather than a lookup**, and it stays one entry long for the
 * reason the module header gives for resolving the other 250 through the platform — translating
 * names by hand is 500 strings to keep in parity and get wrong.
 *
 * Forgetting an entry here is not a silent failure: `Intl.DisplayNames` is constructed with
 * `fallback: 'code'`, so an unknown code returns itself, and `i18n/parity.ts` fails
 * `locale.country-untranslated` on exactly `nameOf(code, locale) === code`, in both languages.
 */
const SUPPLIED_COUNTRY_NAMES = Object.freeze({
    XX: { es: 'Apátrida o sin determinar', en: 'Stateless or undetermined' },
});
/**
 * Every country as a select option, sorted by name with the shared collator.
 *
 * Sorted by NAME rather than by code, because that is the order a person scanning a list expects,
 * and with `compareForSort` so that the order is total and therefore stable between renders.
 */
export function countryOptions(locale) {
    return COUNTRY_CODES.map((value) => ({ value, label: countryName(value, locale) })).sort((left, right) => compareForSort(left.label, right.label));
}
//# sourceMappingURL=countries.js.map